/* VALIDATORS */
import {isValidPhoneNumber, findPhoneNumbersInText} from 'libphonenumber-js';
import {isValid as isValidEmail} from 'mailchecker';
import * as IBAN from 'iban';
import BIC from 'bic';
import {helpers} from '@vuelidate/validators';

/* MIXINS */
import pwdRulesMixin from '@mixins/pwdRulesMixin';

export const roomValidator = (value) => {
  return value.zoneId !== undefined;
};

export const mustBeCool = (value) => {
  return value !== null;
};

/*
 * Variantes de minLength/maxLength qui ignorent les espaces en fin de chaîne
 */
export const minLengthTrimEnd = (min) =>
  helpers.withParams(
    {type: 'minLength', min},
    (value) => !helpers.req(value) || value.replace(/\s+$/, '').length >= min,
  );

export const maxLengthTrimEnd = (max) =>
  helpers.withParams(
    {type: 'maxLength', max},
    (value) => !helpers.req(value) || value.replace(/\s+$/, '').length <= max,
  );

export default {
  mixins: [pwdRulesMixin],
  methods: {
    phoneValidator(value) {
      return !value || isValidPhoneNumber(value);
    },

    emailValidator(value) {
      return !value || isValidEmail(value);
    },

    /*
     * Regex de Yousign pour valider un nom / prénom de signataire
     */
    yousignValidator(value) {
      return (
        !value ||
        /^(?!\s)[0-9A-Za-zĄÀÁÂÃÄÅÇĆÈÉÊËĘÌÍÎÏŁÑŃÒÓÔÕÖŚÙÚÛÜÝŹŻąàáâãäåæçćèéêëęìíîïłñńòóôõöśùúûüýÿźżßĀāĂăĈĉĊċČčĎďĐđĒēĔĕĖėĚěĜĝĞğĠġĢģĤĥĦħĨĩĪīĬĭĮįİıĲĳĴĵĶķĸĹĺĻļĽľĿŀŅņŇňŉŊŋŌōŎŏŐőŒœŔŕŖŗŘřŜŝŞşŠšŢţŤťŦŧŨũŪūŬŭŮůŰűŲųŴŵŶŷŸŽžſøØÆȘșȚț`'()\- ]+(?<!\s)$/.test(
          value,
        )
      );
    },
    pwdValidator(value) {
      return !value || this.isValidPwd(value);
    },

    /* INFORMATIONS BANCAIRES */
    ibanValidator(value) {
      return !value || IBAN.isValid(value);
    },
    bicValidator(value) {
      return !value || BIC.isValid(value);
    },
    accommodationTitleHasTooManyUppercase(value) {
      const uppercaseCount = (value.match(/[A-Z]/g) || []).length;
      return uppercaseCount > 5 || /[A-Z]{4,}/.test(value);
    },
    /*
     * Résumé/Description : pas de limite sur le nombre total de majuscules (bloc de texte
     * long), seulement sur les majuscules consécutives (GS-520, retour Dora du 09/09).
     */
    accommodationDescriptionHasTooManyUppercase(value) {
      return /[A-Z]{6,}/.test(value);
    },
    textHasEmoji(value) {
      return /\p{Emoji_Presentation}|\p{Extended_Pictographic}️|\p{Regional_Indicator}/u.test(value);
    },
    textHasUrl(value) {
      // évite de matcher le domaine d'un email (ex. "akelio.fr" dans "m.legal@akelio.fr")
      const withoutEmails = value.replace(/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/gi, '');
      return (
        /(https?:\/\/|www\.)\S+/i.test(withoutEmails) || /\b[a-z0-9-]+(\.[a-z0-9-]+)*\.[a-z]{2,}\b/i.test(withoutEmails)
      );
    },
    textHasEmail(value) {
      return /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/i.test(value);
    },
    textHasPhone(value) {
      return findPhoneNumbersInText(value, 'FR').length > 0;
    },
    /*
     * Combine les règles emoji + majuscules + lien + email + téléphone sous un seul
     * customPattern (AkInputText n'affiche un message que pour une clé "customPattern").
     * Règle du TITRE d'annonce, plus stricte que résumé/description (GS-520).
     */
    accommodationTitleValidator(value) {
      if (!value) return true;
      return (
        !this.accommodationTitleHasTooManyUppercase(value) &&
        !this.textHasEmoji(value) &&
        !this.textHasUrl(value) &&
        !this.textHasEmail(value) &&
        !this.textHasPhone(value)
      );
    },
    accommodationTitleErrorMessage(value) {
      if (!value) return '';
      if (this.textHasEmoji(value)) {
        return this.$t('accommodation.content_error_emoji');
      }
      if (this.textHasUrl(value)) {
        return this.$t('accommodation.content_error_url');
      }
      if (this.textHasEmail(value)) {
        return this.$t('accommodation.content_error_email');
      }
      if (this.textHasPhone(value)) {
        return this.$t('accommodation.content_error_phone');
      }
      return this.$t('accommodation.content_error_uppercase');
    },
    /*
     * Règle du RÉSUMÉ/DESCRIPTION d'annonce : moins stricte que le titre (GS-520).
     * Pas de doc officielle Airbnb sur ces 2 champs — testé en sync réelle critère par critère.
     * emoji : confirmé accepté par Airbnb sur ces 2 champs, désactivé ici (non appliqué) mais
     * fonction conservée, réactivable si besoin (textHasEmoji).
     * majuscules excessives : réactivé à la demande produit (blocage préventif indépendant du
     * rejet Airbnb constaté).
     * lien/email/téléphone : conservés bloqués (choix produit, indépendant du rejet Airbnb).
     */
    accommodationDescriptionValidator(value) {
      if (!value) return true;
      return (
        !this.accommodationDescriptionHasTooManyUppercase(value) &&
        !this.textHasUrl(value) &&
        !this.textHasEmail(value) &&
        !this.textHasPhone(value)
      );
    },
    accommodationDescriptionErrorMessage(value) {
      if (!value) return '';
      if (this.textHasUrl(value)) {
        return this.$t('accommodation.content_error_url');
      }
      if (this.textHasEmail(value)) {
        return this.$t('accommodation.content_error_email');
      }
      if (this.textHasPhone(value)) {
        return this.$t('accommodation.content_error_phone');
      }
      if (this.accommodationDescriptionHasTooManyUppercase(value)) {
        return this.$t('accommodation.content_error_uppercase_consecutive');
      }
      return '';
    },
  },
};
